"use client"

import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { collection, query, orderBy, limit, getDocs } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { Clock, HelpCircle, ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"

export default function QuizSection() {
  const [quizzes, setQuizzes] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchLatestQuizzes = async () => {
      try {
        const q = query(collection(db, "quizzes"), orderBy("createdAt", "desc"))
        const snapshot = await getDocs(q)
        const allQuizzes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        // Filter: Only visible ones AND exclude UPSC
        const filteredQuizzes = allQuizzes.filter((q: any) => {
           const isVisible = q.isVisible !== false;
           const isNotUPSC = !q.title?.toLowerCase().includes("upsc");
           return isVisible && isNotUPSC;
        });
        setQuizzes(filteredQuizzes.slice(0, 4))
      } catch (error) {
        console.error("Failed to load quizzes", error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchLatestQuizzes()
  }, [])

  if (isLoading || quizzes.length === 0) return null

  return (
    <section className="relative py-16 lg:py-24 bg-white border-b border-slate-100 overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6 sm:px-8 relative z-10">
        
        {/* Header */}
        <div className="mb-10 sm:mb-16 border-l-2 border-[#b48c36] pl-5 py-1">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-1"
          >
            <p className="text-[#b48c36] font-bold text-[10px] uppercase tracking-[0.4em]">
              Daily Assessment
            </p>
            <h2 className="text-3xl font-serif font-medium text-slate-950 tracking-tight leading-none">
              Live <span className="text-[#b48c36]">Quizzes</span>
            </h2>
          </motion.div>
        </div>

        {/* Quizzes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quizzes.map((quiz, idx) => (
            <motion.div
              key={quiz.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="group relative bg-slate-50 border border-slate-200 p-6 sm:p-8 hover:bg-slate-950 hover:border-slate-900 transition-all duration-500 rounded-sm flex flex-col items-start justify-between min-h-[220px]"
            >
               <div className="space-y-4 w-full">
                  <div className="w-10 h-10 bg-white border border-slate-200 group-hover:border-slate-800 flex items-center justify-center text-[#b48c36]">
                     <HelpCircle size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-serif font-bold text-slate-900 group-hover:text-white transition-colors mb-2 line-clamp-2">
                      {quiz.title}
                    </h3>
                    <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-widest text-slate-500 group-hover:text-slate-400">
                      <span className="flex items-center gap-1.5"><Clock size={12}/> {quiz.duration} Mins</span>
                      <span className="flex items-center gap-1.5"><HelpCircle size={12}/> {quiz.questions?.length || 0} Qs</span>
                    </div>
                  </div>
               </div>
               
               <Link 
                 to={`/quiz/${quiz.slug}`} 
                 className="mt-6 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#b48c36] hover:text-white transition-colors group/link w-full border-t border-slate-200 group-hover:border-slate-800 pt-4"
               >
                 Start Assessment <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
               </Link>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  )
}
